import { Button, Icon, IconButton, makeStyles, Paper, TextField, Theme } from '@material-ui/core';
import { Entity } from '@moderepo/mode-apis';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompSaveAction, BaseInfoCompProps, useModePanelStyle, useModeFormStyle, InfoCompPanelHeader,
    ModeInfoCompTextInputField, ModeInfoCompAutoCompleteInputField, inputStateSetterHelper, FontIcon, ModeInfoCompSelectInputField,
    usePropValue, BaseEntityFieldArray, ModeFormInputSection, ModeInfoCompCheckBoxInputField } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { useCallback } from 'react';
import { SelectEntityDropdownMenuComp, SelectEntityDropdownMenuCompProps } from '..';
import { EntitiesListingContainerProps, EntitiesMapDashboardWidgetInputs } from '../../containers';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        entityIdsInputFieldWrapper: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'flex-start',
            justifyContent: 'flex-start',
        },

        entityIdsInputField: {
            flex    : 1,
            overflow: 'hidden',
        },

        entityIdsInputFieldInput: {
            flexWrap     : 'wrap',
            '&.has-value': {
                // Hide the hidden input field if there are values
                '& input': {
                    display: 'none',
                },
            },
        },

        tag: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            fontSize     : 'small',
            borderRadius : 2,
            border       : `1px solid ${theme.palette.divider}`,
            padding      : '2px 10px',
            margin       : 3,
            background   : theme.palette.background.default,
            '& .label'   : {
                overflow    : 'hidden',
                whiteSpace  : 'nowrap',
                textOverflow: 'ellipsis',
            },
            '& .icon': {
                fontSize  : 'small',
                cursor    : 'pointer',
                marginLeft: 5,
            },
        },

        addEntityButton: {
            marginTop: theme.spacing(2),
            display  : 'inline-block',
            height   : 'fit-content',
        },

    };
}, { name: 'EntitiesMapDashboardWidgetSettingsComp', index: 1 });



/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface EntitiesMapDashboardWidgetActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate?: BaseInfoCompSaveAction<EntitiesMapDashboardWidgetInputs> | undefined
}



export interface EntitiesMapDashboardWidgetSettingsCompProps extends BaseInfoCompProps {
    readonly fields: {
        readonly title: BaseEntityField<string>;
        readonly icon: BaseEntityField<string>;
        readonly homeId: BaseEntityField<number>;
        readonly entityClasses: BaseEntityFieldArray<string>;
        readonly entities: BaseEntityFieldArray<Entity>;
        readonly view: {
            readonly excludeEntitiesWithoutLocation: BaseEntityField<boolean>;
        };
    };

    readonly entitiesListingContainerProps: EntitiesListingContainerProps;
    readonly texts: {
        readonly viewSettings: string;
    } & SelectEntityDropdownMenuCompProps['texts'];

    readonly actions?: EntitiesMapDashboardWidgetActionsSet | undefined;
}



export const EntitiesMapDashboardWidgetSettingsComp: React.FC<EntitiesMapDashboardWidgetSettingsCompProps> = (
    props: EntitiesMapDashboardWidgetSettingsCompProps,
) => {

    const [widgetTitleInput, setWidgetTitleInput] = usePropValue<string | undefined>(props.fields.title.value);
    const [widgetIconInput, setWidgetIconInput] = usePropValue<string | undefined>(props.fields.icon.value);
    const [homeIdInput, setHomeIdInput] = usePropValue<number | undefined>(props.fields.homeId.value);
    const [entityClassesInput, setEntityClassesInput] = usePropValue<readonly string[] | undefined>(props.fields.entityClasses.value);
    const [entitiesInput, setEntitiesInput] = usePropValue<readonly Entity[] | undefined>(props.fields.entities.value);
    const [excludeEntitiesWithoutLocationInput, setExcludeEntitiesWithoutLocationInput] = usePropValue<boolean | undefined>(
        props.fields.view.excludeEntitiesWithoutLocation.value,
    );

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            title                         : setWidgetTitleInput,
            icon                          : setWidgetIconInput,
            homeId                        : setHomeIdInput,
            entityClasses                 : setEntityClassesInput,
            entities                      : setEntitiesInput,
            excludeEntitiesWithoutLocation: setExcludeEntitiesWithoutLocationInput,
        });
    }, [setWidgetTitleInput, setWidgetIconInput, setHomeIdInput, setEntityClassesInput, setEntitiesInput, setExcludeEntitiesWithoutLocationInput]);



    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setWidgetTitleInput(props.fields.title.value);
        setWidgetIconInput(props.fields.icon.value);
        setHomeIdInput(props.fields.homeId.value);
        setEntityClassesInput(props.fields.entityClasses.value);
        setEntitiesInput(props.fields.entities.value);
        setExcludeEntitiesWithoutLocationInput(props.fields.view.excludeEntitiesWithoutLocation.value);
    }, [setWidgetTitleInput, props.fields.title.value, props.fields.icon.value, props.fields.homeId.value, props.fields.entityClasses.value,
        props.fields.entities.value, props.fields.view.excludeEntitiesWithoutLocation, setWidgetIconInput, setHomeIdInput, setEntityClassesInput,
        setEntitiesInput, setExcludeEntitiesWithoutLocationInput]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                title                         : widgetTitleInput?.trim(),
                icon                          : widgetIconInput?.trim(),
                homeId                        : homeIdInput ? Number(homeIdInput) : undefined,
                entityClasses                 : entityClassesInput,
                entities                      : entitiesInput,
                excludeEntitiesWithoutLocation: excludeEntitiesWithoutLocationInput,
            });
        }

        return false;
    }, [props.actions?.saveUpdate, widgetTitleInput, widgetIconInput, homeIdInput, entityClassesInput, entitiesInput,
        excludeEntitiesWithoutLocationInput]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, classes.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <ModeInfoCompTextInputField
                        className={formClasses.formInputField}
                        inputType="text"
                        compProps={props}
                        inputProps={props.fields.title}
                        fieldName="title"
                        inputValue={widgetTitleInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompAutoCompleteInputField
                        className={formClasses.formInputField}
                        fieldName="icon"
                        compProps={props}
                        inputProps={props.fields.icon}
                        inputValue={widgetIconInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompSelectInputField
                        className={formClasses.formInputField}
                        compProps={props}
                        inputProps={props.fields.homeId}
                        fieldName="homeId"
                        inputValue={homeIdInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompAutoCompleteInputField
                        className={formClasses.formInputField}
                        compProps={props}
                        inputProps={props.fields.entityClasses}
                        fieldName="entityClasses"
                        inputValue={entityClassesInput}
                        onInputChange={onInputChange}
                    />

                    {/**
                     * Custom input fields for selecting entities
                     */}
                    <div className={clsx(classes.entityIdsInputFieldWrapper, formClasses.formInputField)}>
                        <TextField
                            className={classes.entityIdsInputField}
                            label={props.fields.entities.label}
                            variant={props.fields.entities.variant ?? 'standard'}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            required={props.fields.entities.required}
                            onClick={() => {
                                if (props.onFieldClicked) {
                                    props.onFieldClicked('entities');
                                }
                            }}
                            InputProps={{
                                readOnly      : true,
                                className     : clsx(classes.entityIdsInputFieldInput, entitiesInput && entitiesInput.length > 0 && 'has-value'),
                                startAdornment: entitiesInput?.map((entity, index) => {
                                    return (
                                        <div
                                            key={entity.entityId}
                                            className={classes.tag}
                                        >
                                            <span className="label">{entity.displaySettings.name}</span>
                                            {props.isEditing && (
                                                <Icon
                                                    className="icon"
                                                    onClick={() => {
                                                        // Make a copy of the current entities array and then remove the item at index
                                                        const newEntitiesArray = [
                                                            ...(entitiesInput ?? []),
                                                        ];
                                                        newEntitiesArray.splice(index, 1);

                                                        // Notify the parent container
                                                        onInputChange('entities', newEntitiesArray);
                                                        if (props.onFormInputChange) {
                                                            props.onFormInputChange('entities', newEntitiesArray);
                                                        }
                                                    }}
                                                    fontSize="small"
                                                >close
                                                </Icon>
                                            )}

                                        </div>
                                    );
                                }),
                            }}
                            error={Boolean(props.fields.entities.error)}
                            helperText={props.fields.entities.error ? props.fields.entities.error : props.fields.entities.description}
                        />
                        {/**
                         * Add Entity dropdown select control to allow the user to add entity
                        */}
                        {props.isEditing && (
                            <SelectEntityDropdownMenuComp
                                className={classes.addEntityButton}
                                togglerComponent={(
                                    <IconButton size="small">
                                        <Icon fontSize="small">add</Icon>
                                    </IconButton>
                                )}
                                entitiesListingContainerProps={props.entitiesListingContainerProps}
                                texts={props.texts}
                                onEntitySelected={(entity) => {
                                    // Make sure the selected entity isn't already selected before adding it to the list of entities
                                    if (!entitiesInput?.includes(entity)) {
                                        const newEntitiesArray = [
                                            ...(entitiesInput ?? []),
                                            entity,
                                        ];

                                        // Notify the parent container
                                        onInputChange('entities', newEntitiesArray);
                                        if (props.onFormInputChange) {
                                            props.onFormInputChange('entities', newEntitiesArray);
                                        }
                                    }
                                    return entitiesInput;
                                }}
                            />
                        )}
                    </div>

                    <ModeFormInputSection
                        collapsible
                        title={props.texts.viewSettings}
                        icon="visibility"
                    >
                        <ModeInfoCompCheckBoxInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.view.excludeEntitiesWithoutLocation}
                            fieldName="excludeEntitiesWithoutLocation"
                            inputValue={excludeEntitiesWithoutLocationInput}
                            onInputChange={onInputChange}
                        />
                    </ModeFormInputSection>


                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </Paper>
    );
};
